/* eslint-disable @typescript-eslint/no-explicit-any */
import { jdiColorFetch } from '../colorFetchV2';
import {
  AddressVo,
  ApplyReasonResVo,
  CalendarData,
  CancelAfsRecorResVo,
  IApplyReasonReq,
  ICalendarListReq,
  ICancelRecordReq,
  IEditReturnInfoReq,
  IPostHouseListReq,
  IRecordApplyCreateReq,
  IRecordApplyInfoReq,
  IRecordDetailReq,
  IRecordListReq,
  IRefundListReq,
  ITraceInfoReq,
  PostHouseListResVo,
  RecordApplyCreateResVo,
  RecordApplyInfoResVo,
  RecordDetailResVo,
  RecordListResVo,
  RecordTraceVo,
  RefundListApiResVo,
  RuleInfoVO,
} from './afterSales.interface';
// 埋点监控
import {
  AFTERSALES_EVENT_NAMES,
  afterSalesBusinessLogger,
} from '@/utils/logger/instance/afterSalesBusinessLogger';

interface AddressListData {
  addressList: AddressVo[];
}
interface ApplyReasonListData {
  data: ApplyReasonResVo[];
}

interface PostHouseListRes {
  data: PostHouseListResVo;
}

interface RecordApplyInfoData {
  data: RecordApplyInfoResVo;
}

interface RecordApplyCreateRes {
  data: RecordApplyCreateResVo;
}

interface RecordListRes {
  data: RecordListResVo[];
}

interface RecordDetailRes {
  data: RecordDetailResVo;
}

interface CancelAfsRecorRes {
  data: CancelAfsRecorResVo;
}

interface RuleInfoRes {
  ruleVOList: RuleInfoVO[];
}

interface RecordTraceRes {
  data: RecordTraceVo;
}

interface CalendarRes {
  data: CalendarData[];
}
// 查询地址列表的 API 方法
export const fetchAddressList = async (): Promise<AddressVo[]> => {
  const response = await jdiColorFetch('user_address_getList', {
    bodyQuery: {},
  });
  return response?.data?.addressList || [];
};

// 查询支持的售后原因
export const fetchApplyReasonList = async (
  requestData: IApplyReasonReq,
): Promise<ApplyReasonResVo[]> => {
  const response = await jdiColorFetch(
    'aftersale_refund_getApplyReason',
    {
      bodyQuery: {
        ...requestData,
      },
    },
    {
      needUniformBizInfo: true,
    },
  );
  return response?.data;
};

// 获取驿站信息列表
export const fetchPostHouseList = async (
  requestData: IPostHouseListReq,
): Promise<PostHouseListResVo[]> => {
  const response = await jdiColorFetch(
    'aftersale_refund_getPostHouseInfo',
    {
      bodyQuery: {
        ...requestData,
      },
    },
    { needUniformBizInfo: true, isCommonArea: false },
  );
  return response?.data || [];
};

// 进入售后单申请详情页，订单以及退款相关信息
export const fetchRecordApplyInfo = async (
  requestData: IRecordApplyInfoReq,
): Promise<RecordApplyInfoResVo> => {
  const response = await jdiColorFetch(
    'aftersale_refund_getItems',
    {
      method: 'post',
      bodyQuery: {
        ...requestData,
      },
    },
    {
      needUniformBizInfo: true,
    },
  );
  if (response?.code !== '200' || !response?.data) {
    // 报错需上报错误日志
    try {
      afterSalesBusinessLogger({
        eventName: AFTERSALES_EVENT_NAMES.AFTERSALES_RETURN_PRODUCT_FAIL,
        message: {
          error: JSON.stringify(response),
        },
      });
    } catch (err) {}
  }
  return response?.data;
};

// 创建售后服务单
export const recordApplyCreate = async (
  requestData: IRecordApplyCreateReq,
): Promise<RecordApplyCreateResVo> => {
  const response = await jdiColorFetch(
    'aftersale_refund_add',
    {
      bodyQuery: {
        ...requestData,
      },
    },
    {
      needUniformBizInfo: true,
      isCommonArea: false,
    },
  );
  if (response?.code !== '200' || !response?.data) {
    // 报错需上报错误日志
    try {
      afterSalesBusinessLogger({
        eventName: AFTERSALES_EVENT_NAMES.AFTERSALES_APPLY_FAIL,
        message: {
          error: JSON.stringify(response),
        },
      });
    } catch (err) {}
  }
  return response;
};

// 售后记录列表
export const fetchRecordList = async (requestData: IRecordListReq): Promise<RecordListResVo[]> => {
  const response = await jdiColorFetch(
    'aftersale_refund_getListByOrderId',
    {
      bodyQuery: {
        ...requestData,
      },
    },
    {
      needUniformBizInfo: true,
    },
  );
  if (response?.code !== '200' || !response?.data) {
    // 报错需上报错误日志
    try {
      afterSalesBusinessLogger({
        eventName: AFTERSALES_EVENT_NAMES.AFTERSALES_LIST_PAGE_LOAD_FAIL,
        message: {
          error: JSON.stringify(response),
          pageType: 'order',
        },
      });
    } catch (err) {}
  }
  return response?.data;
};

// 售后服务详情
export const fetchRecordDetail = async (
  requestData: IRecordDetailReq,
): Promise<RecordDetailResVo> => {
  const response = await jdiColorFetch(
    'aftersale_refund_get',
    {
      bodyQuery: {
        ...requestData,
      },
    },
    {
      needUniformBizInfo: true,
    },
  );
  if (response?.code !== '200' || !response?.data) {
    // 报错需上报错误日志
    try {
      afterSalesBusinessLogger({
        eventName: AFTERSALES_EVENT_NAMES.AFTERSALES_DETAIL_PAGE_LOAD_FAIL,
        message: {
          error: JSON.stringify(response),
        },
      });
    } catch (err) {}
  }
  return response?.data;
};

// 取消提交的售后单
export const cancelAfsRecord = async (
  requestData: ICancelRecordReq,
): Promise<CancelAfsRecorResVo> => {
  const response = await jdiColorFetch(
    'aftersale_refund_cancel',
    {
      bodyQuery: {
        ...requestData,
      },
    },
    {
      needUniformBizInfo: true,
    },
  );
  return response;
};

// 获取该用户名下的售后单列表
export const fetchRefundList = async (requestData: IRefundListReq): Promise<RefundListApiResVo> => {
  const response = await jdiColorFetch(
    'aftersale_refund_getList',
    {
      bodyQuery: {
        ...requestData,
      },
    },
    {
      needUniformBizInfo: true,
    },
  );
  if (response?.code !== '200' || !response?.data) {
    // 报错需上报错误日志
    try {
      afterSalesBusinessLogger({
        eventName: AFTERSALES_EVENT_NAMES.AFTERSALES_LIST_PAGE_LOAD_FAIL,
        message: {
          error: JSON.stringify(response),
          pageType: 'afterSales',
        },
      });
    } catch (err) {}
  }
  return response;
};

// 获取售后配置信息
export const fetchRuleInfo = async (): Promise<RuleInfoRes> => {
  const response = await jdiColorFetch(
    'aftersale_refund_getRuleInfo',
    {
      bodyQuery: {},
    },
    {
      needUniformBizInfo: true,
    },
  );
  return response?.data;
};

// 物流轨迹接口
export const fetchTraceInfo = async (requestData: ITraceInfoReq): Promise<RecordTraceVo> => {
  const response = await jdiColorFetch(
    'aftersale_refund_getTrace',
    {
      bodyQuery: {
        ...requestData,
      },
    },
    {
      needUniformBizInfo: true,
    },
  );
  return response?.data;
};

// 获取上门取件日历列表
export const fetchCalendarList = async (requestData: ICalendarListReq): Promise<CalendarData[]> => {
  const response = await jdiColorFetch(
    'aftersale_return_getPromise',
    {
      bodyQuery: {
        ...requestData,
      },
    },
    {
      needUniformBizInfo: true,
    },
  );
  return response?.data?.calendarInfoList;
};

// 修改服务单数据（包括改上门取件时间以及驿站）
export const editReturnInfo = async (
  requestData: IEditReturnInfoReq,
): Promise<CancelAfsRecorResVo> => {
  const response = await jdiColorFetch(
    'aftersale_return_modify',
    {
      bodyQuery: {
        ...requestData,
      },
    },
    {
      needUniformBizInfo: true,
    },
  );
  return response;
};
